import { useState, useEffect } from "react";

type StorageValue = string | number | boolean | object;

interface UseSessionStorageOptions<T extends StorageValue> {
  key: string;
  initialValue: T;
  onError?: (error: Error) => void;
}

export function useSessionStorage<T extends StorageValue>({
  key,
  initialValue,
  onError,
}: UseSessionStorageOptions<T>): [T, (value: T | ((val: T) => T)) => void] {
  // Initialize state with initialValue
  const [storedValue, setStoredValue] = useState<T>(initialValue);

  // Helper function to determine if value needs JSON serialization
  const needsSerialization = (value: unknown): boolean => {
    return typeof value === "object" && value !== null;
  };

  // Helper function to safely parse stored value
  const parseStoredValue = (value: string): T => {
    try {
      return JSON.parse(value) as T;
    } catch {
      // If JSON parsing fails, return the value as is
      return value as unknown as T;
    }
  };

  // Load value from sessionStorage on mount
  useEffect(() => {
    try {
      const item = sessionStorage.getItem(key);
      if (item !== null) {
        setStoredValue(parseStoredValue(item));
      }
    } catch (error) {
      const storageError =
        error instanceof Error
          ? error
          : new Error("Failed to read from sessionStorage"); // eslint-disable-line
      console.error(
        `Error reading from sessionStorage key "${key}":`,
        storageError,
      );
      onError?.(storageError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  // Return a wrapped version of useState's setter function that persists the new value to sessionStorage
  const setValue = (value: T | ((val: T) => T)) => {
    try {
      // Allow value to be a function so we have same API as useState
      const valueToStore =
        value instanceof Function ? value(storedValue) : value;

      // Save state
      setStoredValue(valueToStore);

      // Save to sessionStorage - only serialize if it's an object
      const valueToSave = needsSerialization(valueToStore)
        ? JSON.stringify(valueToStore)
        : String(valueToStore);

      sessionStorage.setItem(key, valueToSave);
    } catch (error) {
      const storageError =
        error instanceof Error
          ? error
          : new Error("Failed to write to sessionStorage"); // eslint-disable-line
      console.error(
        `Error writing to sessionStorage key "${key}":`,
        storageError,
      );
      onError?.(storageError);
    }
  };

  return [storedValue, setValue];
}
